import { Button } from '@/components/Button';
import { FormCard } from '@/components/FormCard';
import { PasswordInput } from '@/components/PasswordInput';
import { AuthLayout } from '@/layouts/AuthLayout';
import { resetPassword } from '@/services/passwordService';
import { ResetPasswordForm } from '@/types/password';
import { handleError } from '@/utils/handleError';
import { Form, Formik, FormikHelpers } from 'formik';
import { t } from 'i18next';
import toast from 'react-hot-toast';
import { useNavigate, useParams } from 'react-router-dom';
import * as yup from 'yup';

export const ResetPassword = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();

  const initialValues: ResetPasswordForm = {
    password: '',
    password_confirmation: '',
  };

  const validationSchema = yup.object().shape({
    password: yup
      .string()
      .matches(
        /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^a-zA-Z\d\s:]).{6,}$/,
        t('common:validation.password.format'),
      )
      .required(t('common:validation.password.required')),
    password_confirmation: yup
      .string()
      .oneOf(
        [yup.ref('password')],
        t('common:validation.password.confirmation'),
      )
      .required(t('common:validation.password.confirmation')),
  });

  const handleResetPassword = async (
    values: ResetPasswordForm,
    { setSubmitting, setErrors, resetForm }: FormikHelpers<ResetPasswordForm>,
  ) => {
    try {
      const { message } = await resetPassword({ ...values, token });
      toast.success(message);
      resetForm();
      navigate('/');
    } catch (error) {
      handleError(error, setErrors);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AuthLayout
      title={t('resetPassword:welcome')}
      welcomeText={t('resetPassword:welcome-text')}
    >
      <FormCard title={t('resetPassword:title')}>
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleResetPassword}
        >
          {({ isSubmitting, touched, errors }) => (
            <Form className="w-full space-y-4 md:space-y-5">
              <div className="space-y-2">
                <PasswordInput
                  label={t('resetPassword:label.password')}
                  name="password"
                  placeholder={t('resetPassword:placeholder.password')}
                  error={!!touched.password && !!errors.password}
                />
              </div>

              <div className="space-y-2">
                <PasswordInput
                  label={t('resetPassword:label.password-confirmation')}
                  name="password_confirmation"
                  placeholder={t(
                    'resetPassword:placeholder.password-confirmation',
                  )}
                  error={
                    !!touched.password_confirmation &&
                    !!errors.password_confirmation
                  }
                />
              </div>

              <Button
                type="submit"
                loading={isSubmitting}
                className="mt-4 !w-full bg-gradient-to-r from-sky-600 to-sky-500 py-3 text-white shadow-md hover:from-sky-700 hover:to-sky-600 focus:ring-2 focus:ring-sky-300 md:mt-6"
              >
                {t('resetPassword:submit')}
              </Button>
            </Form>
          )}
        </Formik>
      </FormCard>
    </AuthLayout>
  );
};
